const API_ROOT = `${window.Sonarr.urlBase}/api/v2`;
const ACCESS_TOKEN_KEY = 'accessToken';

export function getTdarrAccessToken() {
  return window.localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setTdarrAccessToken(token: string) {
  window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function clearTdarrAccessToken() {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export function getTdarrTokenUsername() {
  const token = getTdarrAccessToken();
  if (!token) return '';
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(window.atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='))) as { username?: string };
    return decoded.username ?? '';
  } catch {
    return '';
  }
}

export class TdarrApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: unknown) {
    super(message);
  }
}

export async function tdarrRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getTdarrAccessToken();
  const response = await fetch(`${API_ROOT}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      'x-tdarr-request-source': 'webui',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers
    }
  });
  const contentType = response.headers.get('content-type') ?? '';
  const body = contentType.includes('application/json') ? await response.json() : await response.text();

  if (!response.ok) {
    throw new TdarrApiError(`Tdarr request failed (${response.status})`, response.status, body);
  }

  return body as T;
}

export function tdarrGet<T>(path: string) {
  return tdarrRequest<T>(path);
}

export function tdarrPost<T>(path: string, data: unknown = {}) {
  return tdarrRequest<T>(path, { method: 'POST', body: JSON.stringify(data) });
}

export function tdarrCrud<T>(data: Record<string, unknown>) {
  return tdarrPost<T>('/cruddb', { data });
}

export interface ClientTableRequest {
  start?: number;
  pageSize?: number;
  filters?: Array<{ id: string; value: string }>;
  sorts?: Array<{ id: string; desc: boolean }>;
  opts?: Record<string, unknown>;
}

export function tdarrClient<T>(clientType: string, request: ClientTableRequest = {}) {
  const { start = 0, pageSize = 25, filters = [], sorts = [], opts = {} } = request;
  return tdarrPost<T>(`/client/${clientType}`, {
    data: { start, pageSize, filters, sorts, opts }
  });
}
